import React, { useCallback, useState, useEffect, useRef } from 'react';
import OneSchemaImporter, { OneSchemaError } from '@oneschema/react';
import { useFlags } from '../../../../../helpers/useFlags';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import { fetchSearch, getSearches } from 'services/inventory/redux';
import { transformItem, Item } from 'services/items';
import {
  TrackingGroup,
  ItemInventoryEvents,
  postInventoryEvent,
  itemHasTracking,
} from 'services/inventory';
import { fetchLocations, getLocations } from 'services/locations';
import { fetchVendors } from 'services/vendors';
import { fetchUoms } from 'services/uoms';
import { fetchTaxRates } from 'services/taxRates';
import { fetchTrackingTypes } from 'services/settings/tracking';
import { Pagination } from 'services/search';
import { initialItem } from 'services/items/consts';
import { exportSampleCSV, ReportId } from 'services/reports';
import { useUrlQueryObject } from 'services/url';
import {
  showDownloadNotification,
  showLoadingNotification,
  showNotification,
} from 'services/api/notifications';
import { ExportSampleType, getErrorMessage } from 'helpers';
import { PaperSlidingLayout } from 'ui/components/Paper/PaperSlidingLayout';
import { PageWithAdvancedSearch } from 'ui/components/Page/PageWithAdvancedSearch';
import { withSearchResults } from 'ui/components/Page/WithSearchResults';
import { ReportsModal } from 'ui/components/Modal/ReportsModal';
import { showProgressAlert } from 'services/alert/redux';
import {
  startBackgroundImport,
  BackgroundType,
  BackgroundAction,
  startBackgroundExport,
  useBackgroundTasks,
} from 'services/backgroundTasks';
import { useOneSchemaToken } from '../../../../../serviceModule/hooks/useOneSchemaToken';
import { fetchTagsAPI, Tag } from 'services/tags';
import {
  clearModuleNavigation,
  ModuleNavigationType,
  removeModuleNavigation,
} from 'services/moduleNavigation';
import { fetchSettingsCompanies } from 'services/settings/company';
import { Routes } from '../../navigation';
import {
  ItemSearchResults,
  ItemsAdvancedSearch,
  ItemDetailsCard,
  InventoryEventModal,
} from './components';
import {
  InventoryEventFormValues,
  transformToInventoryEventData,
} from './components/InventoryEventModal';
import { InventoryPageAction } from './components/ItemSearchResults/consts';
import {
  initialInventoryFormValues,
  advancedSearchReduxActions,
  ITEM_COLUMNS,
  initialPagination,
  displayNameMap,
  createDisplayValueMap,
} from './consts';
import { TrackingPageProps, TrackingPageCmp } from './types';
import { logErrorCtx } from 'app/logging';
import { getSettingsCompany } from 'services/settings/company';
import { CycleEventModal } from './components/CycleEventModal';
import { ModuleNavigation } from 'app/components/AppBar/components';
import QuickTransferModal from './components/QuickTransferModal/QuickTransferModal';
import { useLocation, useNavigate } from 'react-router-dom';

const TrackingPage: TrackingPageCmp = (props: TrackingPageProps) => {
  const {
    searchState: searchResult,
    refreshSearchState: fetchSearchResult,
    isLoadingSearchState: isLoadingSearchResult,
    activeItemId,
  } = props;

  const { items: locations } = useSelector(getLocations);
  const company = useSelector(getSettingsCompany);

  const [activeInventoryItem, setActiveInventoryItem] =
    useState<Item>(initialItem);
  const [eventType, setEventType] = useState<ItemInventoryEvents>(
    ItemInventoryEvents.Add
  );
  const [showEventModal, setShowEventModal] = useState<boolean>(false);
  const [showReport, setShowReport] = useState(false);
  const [showQuickTransferModal, setShowQuickTransferModal] = useState(false);
  const [searchTags, setSearchTags] = useState<Tag[]>([]);
  const [duplicateArray, setDuplicateArray] = useState<string[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const [, extendUrlQuery] = useUrlQueryObject(navigate, location);
  const dispatch = useDispatch();

  const { startFetching, startCsvFetching } = useBackgroundTasks();
  const [showOneSchemaImporter, setShowOneSchemaImporter] = useState(false);
  const hiddenInput = useRef<HTMLInputElement>(null);
  const flags = useFlags();
  const {
    token: oneSchemaToken,
    loading: oneSchemaTokenLoading,
    error: oneSchemaTokenError,
    clientId: oneSchemaClientId,
    templateKeys: oneSchemaTemplateKeys,
  } = useOneSchemaToken({ enabled: flags.enableOneSchemaInventoryImport });

  const hasTracking = itemHasTracking(activeInventoryItem);

  const handleCloseItemInfoCard = () => extendUrlQuery({ activeId: null });

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult({ pagination: newPagination });
  };

  const handleRowThreeDotMenuClick = async (
    item: Item,
    type: ItemInventoryEvents
  ) => {
    setActiveInventoryItem(item);
    setEventType(type);
    setShowEventModal(true);
  };

  const handleEventModalSave = async (
    formValues: InventoryEventFormValues,
    trackingGroups: TrackingGroup[]
  ) => {
    const inventoryEventData = transformToInventoryEventData(
      eventType,
      activeInventoryItem.id!,
      formValues,
      trackingGroups,
      company?.homeCurrency?.code ?? 'USD'
    );

    await postInventoryEvent(
      eventType,
      inventoryEventData,
      hasTracking,
      setDuplicateArray
    );
    await fetchSearchResult();
  };

  const handleCancelModalClicked = () => {
    closeModals();
  };

  //this will run after every postInventoryEvent call
  useEffect(() => {
    if (!duplicateArray.length) {
      setShowEventModal(false);
    }
  }, [duplicateArray]);

  // watch advanced search columns and fetch selected tags
  useEffect(() => {
    const tagIds = _.get(
      searchResult.advancedSearch.columns,
      ['tags.id'],
      null
    ) as number[];

    if (tagIds) {
      (async () => {
        try {
          const resTags = await fetchTagsAPI({}, tagIds);

          setSearchTags(resTags.data);
        } catch {
          //ignore Error
        }
      })();
    }
  }, [searchResult.advancedSearch.columns]);

  useEffect(() => {
    dispatch(clearModuleNavigation(ModuleNavigationType.Materials));

    return () => {
      dispatch(removeModuleNavigation());
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportInventorySample = useCallback(async (type: ExportSampleType) => {
    try {
      await exportSampleCSV(type);
    } catch {
      return;
    }
  }, []);

  const exportInventory = useCallback(async () => {
    showDownloadNotification();
    try {
      await startBackgroundExport(BackgroundType.Inventory);
      startFetching();
    } catch {
      // continue regardless of error
    }
  }, [startFetching]);

  const handleHiddenInput = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        return;
      }

      const uploadedFile = event.target.files[0];
      showLoadingNotification('Your Inventory import has been initiated.');

      startCsvFetching();

      const InventoryName = BackgroundType.Cycle;

      // reset file to default value
      event.target.value = '';
      try {
        await startBackgroundImport(InventoryName, uploadedFile);
        dispatch(
          showProgressAlert(
            'Your Import to Inventory has been initiated. This may take a few minutes to complete.',
            InventoryName,
            BackgroundAction.Import
          )
        );
      } catch (e) {
        const error = e as Error;
        const message = getErrorMessage(error);
        logErrorCtx('Error in startBackgroundImport', {
          error,
          stackTrace: error.stack,
          title: 'Background Import Failed',
          description: message,
          component: 'InventoryPage',
        });
        return;
      }
    },
    [dispatch, startCsvFetching] // Added startCsvFetching to dependency array
  );

  const importInventory = useCallback(() => {
    hiddenInput.current?.click();
  }, [hiddenInput]);

  const handleInventoryPageAction = useCallback(
    (action: InventoryPageAction) => {
      switch (action) {
        case InventoryPageAction.ImportInventory:
          if (flags.enableOneSchemaInventoryImport) {
            setShowOneSchemaImporter(true);
          } else {
            importInventory();
          }
          break;
        case InventoryPageAction.ExportAddInventorySample:
          exportInventorySample(ExportSampleType.InventoryExportAddSample);
          break;
        case InventoryPageAction.ExportCycleInventorySample:
          exportInventorySample(ExportSampleType.InventoryExportCycleSample);
          break;
        case InventoryPageAction.ExportInventory:
          exportInventory();
          break;
        case InventoryPageAction.ShowReport:
          setShowReport(true);
      }
    },
    [
      exportInventorySample,
      exportInventory,
      setShowOneSchemaImporter,
      importInventory,
      flags,
    ]
  );

  const closeModals = () => {
    setShowEventModal(false);
    setActiveInventoryItem(initialItem);
    setDuplicateArray([]);
  };

  const handleItemClicked = (id: number | null) =>
    extendUrlQuery({ activeId: id });

  const handleOneSchemaError = (error: any) => {
    setShowOneSchemaImporter(false);

    showNotification('OneSchema Importer Error', {
      variant: 'error',
    });

    logErrorCtx('OneSchema Importer Error', {
      error,
      title: 'OneSchema Importer Error',
      description: typeof error === 'string' ? error : JSON.stringify(error),
      component: 'InventoryPage',
    });
  };

  return (
    <>
      <ModuleNavigation />
      <PageWithAdvancedSearch
        detailCardColumns={ITEM_COLUMNS}
        initialFormValues={initialInventoryFormValues}
        advancedSearchReduxActions={advancedSearchReduxActions}
        searchResult={searchResult}
        displayNameMap={displayNameMap}
        displayValueMap={createDisplayValueMap(locations, searchTags)}
        fetchSearchResult={fetchSearchResult}
        AdvancedSearchFieldsCmp={ItemsAdvancedSearch}
        pageName="Inventory"
      >
        <PaperSlidingLayout shown={Boolean(activeItemId)}>
          <ItemSearchResults
            items={searchResult.items}
            activeItemId={activeItemId}
            handleItemClick={handleItemClicked}
            isLoadingItems={isLoadingSearchResult}
            pagination={searchResult.pagination || initialPagination}
            onPaginationChange={handlePaginationChange}
            onRowAction={handleRowThreeDotMenuClick}
            onInventoryPageAction={handleInventoryPageAction}
            setShowQuickTransferModal={setShowQuickTransferModal}
          />
          <ItemDetailsCard
            activeItemId={activeItemId}
            onCancel={handleCloseItemInfoCard}
            fetchSearchResult={fetchSearchResult}
            duplicateArray={duplicateArray}
            setDuplicateArray={setDuplicateArray}
          />
        </PaperSlidingLayout>
      </PageWithAdvancedSearch>

      <ReportsModal
        isOpen={showReport}
        reportId={ReportId.Items}
        params={{}}
        onClose={() => setShowReport(false)}
        autoGenerate
      />
      <QuickTransferModal
        show={showQuickTransferModal}
        setShowQuickTransferModal={setShowQuickTransferModal}
        item={activeInventoryItem}
        locationId={null}
        onCancelClicked={() => setShowQuickTransferModal(false)}
      />
      <input
        type="file"
        ref={hiddenInput}
        style={{ display: 'none' }} // Keep original style for hidden input
        onChange={handleHiddenInput} // Keep original handler
        accept=".csv" // Keep original accept attribute
      />

      {eventType === ItemInventoryEvents.Cycle ? (
        <CycleEventModal
          show={showEventModal}
          item={activeInventoryItem}
          locationId={null}
          eventType={ItemInventoryEvents.Cycle}
          onApplyClicked={handleEventModalSave}
          onCancelClicked={handleCancelModalClicked}
          locationDuplicateArray={duplicateArray}
          setShowEventModal={setShowEventModal}
        />
      ) : (
        <InventoryEventModal
          show={showEventModal}
          item={activeInventoryItem}
          eventType={eventType}
          onApplyClicked={handleEventModalSave}
          onCancelClicked={handleCancelModalClicked}
        />
      )}
      {flags.enableOneSchemaInventoryImport && (
        <>
          {showOneSchemaImporter && oneSchemaTokenLoading && (
            <p>Loading OneSchema session...</p>
          )}
          {showOneSchemaImporter && oneSchemaTokenError && (
            <p>
              Error initializing OneSchema: {oneSchemaTokenError}. Please try
              again.
            </p>
          )}
          {showOneSchemaImporter &&
            oneSchemaToken &&
            !oneSchemaTokenLoading &&
            !oneSchemaTokenError && (
              <OneSchemaImporter
                isOpen={showOneSchemaImporter}
                onRequestClose={() => setShowOneSchemaImporter(false)}
                clientId={oneSchemaClientId}
                templateKey={oneSchemaTemplateKeys.inventory}
                userJwt={oneSchemaToken}
                devMode={process.env.NODE_ENV === 'development'}
                onSuccess={(data: unknown) => {
                  console.log('OneSchema import successful:', data);
                  // TODO: Process the imported data
                  showNotification(
                    'OneSchema import has not yet been implemented for this feature. If you are seeing this message, please contact support.',
                    {
                      variant: 'error',
                    }
                  );
                  // handleOneSchemaSuccess(data);
                  setShowOneSchemaImporter(false);
                }}
                onError={(error: OneSchemaError) => {
                  console.error('OneSchema import error:', error);
                  handleOneSchemaError(error);
                }}
                className="oneschema-importer-iframe"
                style={{
                  border: 'none',
                  height: '100vh',
                  width: '100vw',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  zIndex: 9999,
                }}
              />
            )}
        </>
      )}
    </>
  );
};

TrackingPage.route = Routes.TrackingPage;

export default withSearchResults<Item>(TrackingPage, {
  url: '/v1/items?itemType=Inventory',
  expand:
    'images,defaultUom.fromConversions,defaultUom.toConversions,itemTrackingTypes.trackingType,itemLocations',
  dataAdapter: transformItem,
  columns: ITEM_COLUMNS,
  quickSearchColumns: ['name', 'upc', 'sku', 'description'],
  fetchSearch,
  getSearches,
  initialPagination,
  rehydrationThunks: [
    fetchVendors,
    fetchUoms,
    fetchLocations,
    fetchTaxRates,
    fetchTrackingTypes,
    fetchSettingsCompanies,
  ],
});
